// PHA-1243: the saved-reports table (Run / Edit / Archive), shared by the Reports page and the
// Billing page's billing reports. Each page passes the reports that live on it.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertDialog, Badge, Button, Flex, Table, Text } from '@radix-ui/themes';
import { toast } from 'react-hot-toast';
import HandleChip from './HandleChip';
import { SCOPE_COLOR, scopeLabel } from './reportLabels';
import { invalidateSavedReports } from './useSavedReports';
import { useOrganization } from '../../contexts/OrganizationContext';
import { customViewsApi, customViewErrorDetails, ReportSummary } from '../../services/api';

interface ReportListProps {
  reports: ReportSummary[];
  canDesign: boolean;
  emptyText: string;
  onChanged: () => void;
}

const ReportList: React.FC<ReportListProps> = ({ reports, canDesign, emptyText, onChanged }) => {
  const navigate = useNavigate();
  const { orgId, isInternal } = useOrganization();
  const [busy, setBusy] = useState<number | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<ReportSummary | null>(null);

  const archive = async (report: ReportSummary) => {
    setBusy(report.id);
    try {
      await customViewsApi.archive(orgId, report.id, 'report');
      toast.success(`"${report.name}" archived`);
      invalidateSavedReports(orgId);
      onChanged();
    } catch (err) {
      toast.error(customViewErrorDetails(err).join(' '));
    } finally {
      setBusy(null);
      setArchiveTarget(null);
    }
  };

  // Phanova-wide reports are Phanova staff's; a network's reports are edited at the network.
  const canEdit = (r: ReportSummary) =>
    canDesign && (r.scope === 'phanova' ? isInternal : !r.inherited);

  return (
    <>
      {reports.length === 0 ? (
        <Text size="2" color="gray">{emptyText}</Text>
      ) : (
        <Table.Root size="2">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Report</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Scope</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Ask Phanova</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell justify="end">Actions</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {reports.map((r) => (
              <Table.Row key={r.id} data-testid="report-row">
                <Table.Cell>
                  <Flex direction="column">
                    <Text weight="medium">{r.name}</Text>
                    {r.description && <Text size="1" color="gray">{r.description}</Text>}
                  </Flex>
                </Table.Cell>
                <Table.Cell>
                  <Flex gap="1" wrap="wrap">
                    <Badge color={SCOPE_COLOR[r.scope]}>{scopeLabel(r.scope, r.organization_name)}</Badge>
                    {r.flagged_stale && <Badge color="red">Needs re-save</Badge>}
                  </Flex>
                </Table.Cell>
                <Table.Cell><HandleChip handle={r.handle} /></Table.Cell>
                <Table.Cell justify="end">
                  <Flex gap="2" justify="end">
                    <Button size="1" onClick={() => navigate(`/reports/${r.id}`)}>Run</Button>
                    {canEdit(r) && (
                      <Button size="1" variant="soft" onClick={() => navigate(`/reports/${r.id}/edit`)}>Edit</Button>
                    )}
                    {canEdit(r) && (
                      <Button size="1" variant="soft" color="red" disabled={busy !== null}
                        onClick={() => setArchiveTarget(r)}>
                        Archive
                      </Button>
                    )}
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
      <AlertDialog.Root open={!!archiveTarget} onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}>
        <AlertDialog.Content style={{ maxWidth: 440 }}>
          <AlertDialog.Title>Archive "{archiveTarget?.name}"?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Nobody will be able to run this report, here or by #{archiveTarget?.handle} in Ask Phanova.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel><Button variant="soft" color="gray">Cancel</Button></AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button color="red" onClick={() => { if (archiveTarget) void archive(archiveTarget); }}>Archive</Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </>
  );
};

export default ReportList;
